import React, {useEffect, useState, useMemo} from "react";
import {Card, Form, Button, DatePicker, Table, Space, message, Row, Col, Typography, Tag, Tooltip} from "antd";
import {FileExcelOutlined, SearchOutlined, FilePdfOutlined} from "@ant-design/icons";
import apiClient from './apiHelper';
import {ENDPOINTS, REQUEST_TYPES, MESSAGES} from './apiConfig';
import {excelReportColumns, REPORT_TYPE_OPTIONS} from './constants';
import {FilterSelect, ReportPreviewFilterBar} from './FilterBar';


const {Title} = Typography;
const {RangePicker} = DatePicker;

const Reports = ({user}) => {
    const [form] = Form.useForm();
    const [users, setUsers] = useState();
    const [projects, setProjects] = useState();
    const [reportData, setReportData] = useState([]);
    const [timesheetType, setTimesheetType] = useState(null);
    const [tableLoading, setTableLoading] = useState(false);
    const [excelLoading, setExcelLoading] = useState(false);
    const [pdfLoading, setPdfLoading] = useState(false);
    const [userFilterPreview, setUserFilterPreview] = useState(null);
    const [projectFilterPreview, setProjectFilterPreview] = useState(null);

    const userRole = user?.role;
    const reportType = Form.useWatch("report_type", form);

    const fetchUsers = async () => {
        try {
            const response = await apiClient(ENDPOINTS.USER.FETCH_USERS, {
                method: "POST",
                body: JSON.stringify({
                    request_type: REQUEST_TYPES.FETCH_USERS
                }),
            });

            let items = response?.items || response || [];
            if (typeof items === "string") {
                items = JSON.parse(items);
            }

            setUsers(items);

            const projectList = extractUniqueProjects(items);
            setProjects(projectList);

        } catch (error) {
            message.error(MESSAGES.ERROR.FETCH_USERS);
        }
    };

    const extractUniqueProjects = (users) => {
        const projectSet = new Set();

        users.forEach(u => {
            if (u.project) {
                if (Array.isArray(u.project)) {
                    u.project.forEach(p => projectSet.add(p.trim()));
                } else if (typeof u.project === 'string') {
                    u.project.split(',').forEach(p => {
                        const trimmed = p.trim();
                        if (trimmed) projectSet.add(trimmed);
                    });
                }
            }
        });

        return Array.from(projectSet).sort();
    };

    const columns = [
        {
            title: "Date",
            dataIndex: "Date",
            key: "Date",
            width: 110
        },
        {
            title: "User ID",
            dataIndex: "User ID",
            key: "User ID",
            ellipsis: {showTitle: false},
            width: 150,
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            ),
        },
        {
            title: "Project",
            dataIndex: "Project",
            key: "Project",
            width: 150,
            render: (project) => {
                const projectArray = Array.isArray(project)
                    ? project
                    : (project ? String(project).split(',').map(p => p.trim()) : []);

                if (projectArray.length === 0) return <span style={{color: '#bfbfbf'}}>N/A</span>;

                return (
                    <div style={{display: 'flex', flexWrap: 'wrap', gap: '4px'}}>
                        {projectArray.map((p) => (
                            <Tooltip title={p} key={p} mouseEnterDelay={0.5}>
                                <Tag
                                    color="gray"
                                    key={p}
                                    style={{
                                        margin: 0,
                                        fontSize: '11px',
                                        maxWidth: '100px',
                                        display: 'inline-block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                        verticalAlign: 'bottom'
                                    }}
                                >
                                    {p}
                                </Tag>
                            </Tooltip>
                        ))}
                    </div>
                );
            },
        },
        ...excelReportColumns,
        {
            title: "Status",
            dataIndex: "Status",
            key: "Status",
            width: 100,
            render: (status) => {
                const safeStatus = status ? String(status).toLowerCase() : "";

                let color;
                if (safeStatus === "approved") color = "green";
                if (safeStatus === "pending") color = "blue";

                return (
                    <Tag color={color}>
                        {safeStatus ? safeStatus.toUpperCase() : "N/A"}
                    </Tag>
                );
            },
        },
        {
            title: "Approved By",
            dataIndex: "Approved By",
            key: "Approved By",
            width: 120,
            ellipsis: {showTitle: false},
            render: (text) => (
                <Tooltip placement="topLeft" title={text}>
                    {text}
                </Tooltip>
            ),
        }
    ];

    const generateReport = async (values, file_type = "excel") => {
        const isPdf = file_type === "pdf";
        isPdf ? setPdfLoading(true) : setExcelLoading(true);

        try {
            const token = sessionStorage.getItem("token");
            const {date_range, report_type, user_filter, project_filter} = values || {};

            if (!date_range) {
                message.error("Please select a date range.");
                return;
            }
            if (report_type === "project" && !project_filter) {
                message.error("Please select a project filter.");
                return;
            }
            if (report_type === "user" && !user_filter) {
                message.error("Please select a user filter.");
                return;
            }

            const [start, end] = date_range;
            const start_date = start.format("YYYY-MM-DD");
            const end_date = end.format("YYYY-MM-DD");

            const isProjectReport = report_type === "project";

            let requestType;
            if (report_type === "project") {
                requestType = REQUEST_TYPES.GENERATE_PROJECT_REPORT;
            } else if (report_type === "leave") {
                requestType = REQUEST_TYPES.GENERATE_LEAVE_REPORT;
            } else {
                requestType = REQUEST_TYPES.GENERATE_USER_REPORT;
            }

            const body = {
                request_type: requestType,
                start_date,
                end_date,
                preview: false,
                file_type,
                ...(isProjectReport
                        ? {project: project_filter}
                        : {user_id: user_filter || user?.user_id}
                ),
            };

            const response = await apiClient(ENDPOINTS.REPORT.GENERATE_REPORT, {
                method: "POST",
                body: JSON.stringify(body),
                headers: {'Authorization': token},
                responseType: "blob",
            });

            if (response instanceof Blob && response.type === "application/json") {
                const text = await response.text();
                const errorData = JSON.parse(text);
                if (errorData.error) {
                    message.error(errorData.error);
                    return;
                }
            }

            let fileName;
            if (report_type === "leave") {
                fileName = "Leave_Report";
            } else if (isProjectReport) {
                fileName = `Project_${project_filter}`;
            } else {
                fileName = `User_${user_filter || 'Report'}`;
            }

            const mimeType = isPdf
                ? "application/pdf"
                : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
            const extension = isPdf ? "pdf" : "xlsx";

            const blob = new Blob([response], {type: mimeType});
            const href = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = href;
            a.download = `${fileName}_${start_date}.${extension}`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(href);

            message.success(MESSAGES.SUCCESS.GENERATE_REPORT);
        } catch (err) {
            message.error(err?.message || MESSAGES.ERROR.GENERATE_REPORT);
        } finally {
            isPdf ? setPdfLoading(false) : setExcelLoading(false);
        }
    };

    const previewReport = async (values) => {
        setTableLoading(true);
        try {
            const {date_range, project_filter, user_filter, report_type} = values;

            if (!date_range) {
                message.error("Please select date range!");
                return;
            }

            if (report_type === "project" && !project_filter) {
                message.error("Please select project filter!");
                return;
            }

            if (report_type === "user" && !user_filter) {
                message.error("Please select user filter!");
                return;
            }

            const start_date = date_range[0].format("YYYY-MM-DD");
            const end_date = date_range[1].format("YYYY-MM-DD");

            let requestType;
            if (report_type === "project") {
                requestType = REQUEST_TYPES.GENERATE_PROJECT_REPORT;
            } else if (report_type === "leave") {
                requestType = REQUEST_TYPES.GENERATE_LEAVE_REPORT;
            } else {
                requestType = REQUEST_TYPES.GENERATE_USER_REPORT;
            }

            const body = {
                request_type: requestType,
                preview: true,
                start_date,
                end_date,
                ...(report_type === "project"
                        ? {project: project_filter}
                        : {user_id: user_filter || user?.user_id}
                ),
            };

            const response = await apiClient(ENDPOINTS.REPORT.GENERATE_REPORT, {
                method: "POST",
                body: JSON.stringify(body),
            });

            if (response.data && response.data.error) {
                message.error(response.data.error);
                setReportData([]);
                return;
            }

            setReportData(response);

        } catch (err) {
            message.error(err?.message);
            setReportData([]);
        } finally {
            setTableLoading(false);
        }
    };

    const filteredData = useMemo(() => {
        let data = [...reportData];

        if (['user', 'project'].includes(reportType) && timesheetType) {
            data = data.filter(item => item.Type === timesheetType);
        }

        if (reportType === "leave" && userFilterPreview) {
            data = data.filter(item => item["User ID"] === userFilterPreview);
        }

        if (reportType === "leave" && projectFilterPreview) {
            data = data.filter(item =>
                item.Project && item.Project.toLowerCase().includes(projectFilterPreview.toLowerCase())
            );
        }

        return data;
    }, [reportData, timesheetType, userFilterPreview, projectFilterPreview, reportType]);

    useEffect(() => {
        if (user && userRole !== "user") {
            fetchUsers();
        }
    }, [user, userRole]);

    return (
        <div>
            <Row gutter={[16, 16]}>
                <Col span={24}>
                    <Card title={<Title level={4} style={{marginBottom: 0}}>Generate Reports</Title>}>
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={(values) => generateReport(values, "excel")}
                            className="max-w-4xl"
                        >
                            {(userRole !== "user") ?
                                <Row gutter={16}>
                                    <Col span={8}>
                                        <Form.Item
                                            name="report_type"
                                            label="Report Type"
                                            initialValue="user"
                                        >
                                            <FilterSelect
                                                mode="default"
                                                placeholder="Select Report Type"
                                                options={REPORT_TYPE_OPTIONS}
                                                width="100%"
                                            />
                                        </Form.Item>
                                    </Col>
                                    {reportType === "user" &&
                                        <Col span={8}>
                                            <Form.Item
                                                name="user_filter"
                                                label="User Filter"
                                                rules={[{required: true, message: "User filter is required"}]}
                                            >
                                                <FilterSelect
                                                    mode="default"
                                                    placeholder="Select User"
                                                    options={users?.map(u => ({label: u.email, value: u.email})) || []}
                                                    width="100%"
                                                />
                                            </Form.Item>
                                        </Col>
                                    }
                                    {reportType === "project" &&
                                        <Col span={8}>
                                            <Form.Item
                                                name="project_filter"
                                                label="Project Filter"
                                                rules={[{required: true, message: "Project filter is required"}]}
                                            >
                                                <FilterSelect
                                                    mode="default"
                                                    placeholder="Select Project"
                                                    options={projects?.map(p => ({label: p, value: p})) || []}
                                                    width="100%"
                                                />
                                            </Form.Item>
                                        </Col>
                                    }
                                </Row>
                                : []}

                            <Row gutter={16}>
                                <Col span={12}>
                                    <Form.Item
                                        className="red-picker-scope"
                                        name="date_range"
                                        label="Date Range"
                                        rules={[
                                            {required: true, message: "Please select date range!"},
                                        ]}
                                    >
                                        <RangePicker className="w-full red-picker"/>
                                    </Form.Item>
                                </Col>
                                <Col span={12}>
                                    <Space className="pt-[30px]" wrap>
                                        {['manager', 'admin'].includes(userRole) ?
                                            <Button
                                                type="primary"
                                                htmlType="submit"
                                                icon={<FileExcelOutlined/>}
                                                loading={excelLoading}
                                                danger
                                            >
                                                Download Excel
                                            </Button> : []
                                        }
                                        <Button
                                            type="primary"
                                            icon={<FilePdfOutlined/>}
                                            loading={pdfLoading}
                                            onClick={() => {
                                                form.validateFields()
                                                    .then((values) => generateReport(values, "pdf"))
                                                    .catch(() => {
                                                    });
                                            }}
                                            danger
                                        >
                                            Download PDF
                                        </Button>
                                        <Button
                                            onClick={() => previewReport(form.getFieldsValue())}
                                            icon={<SearchOutlined/>}
                                            loading={tableLoading}
                                        >
                                            Preview
                                        </Button>
                                    </Space>
                                </Col>
                            </Row>
                        </Form>
                    </Card>
                </Col>
            </Row>

            {reportData?.length > 0 && (
                <Row gutter={[16, 16]} className="mt-6">
                    <Col span={24}>
                        <Card
                            title="Report Preview"
                            extra={
                                <ReportPreviewFilterBar
                                    reportType={reportType}
                                    timesheetType={timesheetType}
                                    onTimesheetTypeChange={setTimesheetType}
                                    userOptions={users?.map(u => ({label: u.email, value: u.email})) || []}
                                    projectOptions={projects || []}
                                    userFilterValue={userFilterPreview}
                                    projectFilterValue={projectFilterPreview}
                                    onUserFilterChange={setUserFilterPreview}
                                    onProjectFilterChange={setProjectFilterPreview}
                                />
                            }
                        >
                            <Table
                                columns={columns}
                                dataSource={filteredData}
                                rowKey={(record) => `${record.Date}_${record["User ID"]}`}
                                loading={tableLoading}
                                pagination={{pageSize: 10}}
                                tableLayout="fixed"
                            />
                        </Card>
                    </Col>
                </Row>
            )}
        </div>
    );
};

export default Reports;